import {
  apiCallToSaveDatapoint,
  handleSavingDatapoint,
  putDatapoint,
} from "../../api/datapoint";
import { uploadFilesToS3 } from "../../api/fileSync";
import { handleApiError } from "../../components/errorBoundary/errorUtils";
import { isUUID } from "../../utils";
import { db } from "../model";

/**
 * Adds a datapoint to the local IndexedDB database along with associated file data.
 *
 * @param {Object} formData - Form data to be stored.
 * @param {Object} fileAttributes - Files associated with the form data.
 */
async function addDatapoint(formData, fileAttributes, draftId, userDetails = null) {
  try {
    const dataset_id = parseInt(localStorage.getItem("dataset_id"), 10);
    const dataset = await db.datasets.where("id").equals(dataset_id).first();
    const currentUnixTimestamp = Math.floor(Date.now() / 1000);
    const formDataJSON = formData;  

    const datapointID = await db.datapoints.add({
      datasetID: dataset.uuid,
      formData: formDataJSON,
      draft_id:draftId,
      latitude: userDetails?.latitude,
      longitude: userDetails?.longitude,
      timestamp: userDetails?.timestamp,
      submit_time: currentUnixTimestamp,
      syncing: false
    });

    await storeFileData(datapointID, fileAttributes);

    console.log("Datapoint saved successfully");
    return datapointID
  } catch (err) {
    console.log("Error adding datapoint to the offline records:", err);
  }
}

/**
 * Stores file data associated with a datapoint in the local IndexedDB database.
 *
 * @param {number} datapointID - ID of the datapoint to associate the file data with.
 * @param {Object} fileAttributes - Files to be stored.
 */
async function storeFileData(datapointID, fileAttributes) {
  for (const key of Object.keys(fileAttributes)) {
    for (const tmpfileItm of fileAttributes[key]) {
      const fileBlob = tmpfileItm.file;
      const metadata = tmpfileItm.metadata;
      await db.fileData.add({
        datapointID: datapointID,
        data: fileBlob,
        label: key,
        metadata: metadata,
      });
    }
  }
}

/**
 * Asynchronously retrieves file information for a given datapointID from the database.
 *
 * @param {number} datapointID - The ID of the datapoint for which file information is requested (indexedDB ID).
 * @param {string} datapointUUID - The UUID of the datapoint for which file information is requested (backend ID).
 * @returns {Promise<{files: object[]}>} - A Promise that resolves to an object containing file information.
 * @throws {Error} - Throws an error if there is an issue retrieving the file information.
 */
async function createFileInfo(datapointID, datapointUUID) {
  try {
    const fileDataArray = await db.fileData
      .where("datapointID")
      .equals(datapointID)
      .toArray();

    const files = fileDataArray.map((fileData) => ({
      datapointid: datapointUUID,
      label: fileData.label,
      file: fileData.url,
      metadata: fileData.metadata || {},
    }));

    return { files };
  } catch (error) {
    console.error("Error creating file info:", error);
    throw error;
  }
}

/**
 * Updates the S3 file URL in the local IndexedDB database.
 *
 * @param {number} fileID - ID of the file to update.
 * @param {string} url - New S3 file URL.
 */
async function updateS3FileUrl(fileID, url) {
  try {
    const response = await db.fileData
      .where("id")
      .equals(fileID)
      .modify({ url: url });
  } catch (error) {
    console.log(error);
  }
}

/**
 * Updates the S3 URL in the form data and stores it in the local IndexedDB database.
 *
 * @param {number} fileID - ID of the file data.
 * @param {string} url - New S3 URL.
 */
async function updateS3UrlFormdata(fileID, url) {
  try {
    console.log("datapoint file upload sync -> ", fileID, url);
    const file = await db.fileData.where("id").equals(fileID).first();
    const datapoint = await db.datapoints
      .where("id")
      .equals(file.datapointID)
      .first();
    const updatedFormdata = await updateFormData(
      file.label,
      url,
      datapoint.formData
    );
    await db.datapoints
      .where("id")
      .equals(file.datapointID)
      .modify({ formData: updatedFormdata });
  } catch (error) {
    console.log(error);
  }
}

/**
 * Updates form data with a new key-value pair.
 *
 * @param {string} key - Key to update in the form data.
 * @param {string} value - New value for the key.
 * @param {Object} formData - Existing form data to update.
 * @returns {Object} - Updated form data.
 */
async function updateFormData(key, value, formdata) {
  try {
    const keys = key.split(".");
    let currentObj = formdata;
    for (let i = 0; i < keys.length - 1; i++) {
      const currentKey = keys[i];
      currentObj[currentKey] = currentObj[currentKey] || {};
      currentObj = currentObj[currentKey];
    }

    const lastKey = keys[keys.length - 1];

    if (
      Array.isArray(currentObj[lastKey]) &&
      !currentObj[lastKey].includes(value)
    ) {
      currentObj[lastKey].push(value);
    } else {
      currentObj[lastKey] = [value];
    }
    return formdata;
  } catch (error) {
    console.log(error, "issue in updating the formdata");
  }
}

/**
 * Retrieves all datapoints from the local IndexedDB database.
 *
 * @returns {Array} - Array of all datapoints.
 */
async function getAllDataPoints() {
  try {
    const datapoints = await db.datapoints.toArray();
    return datapoints;
  } catch (err) {
    console.log("Error fetching the datapoints from the indexedDB!!!");
  }
}

/**
 * Syncs form data only for a given datapoint.
 *
 * @param {Object} datapoint - Datapoint object to sync.
 * @returns {Object} - Object with status information including 'id' property representing the UUID of the datapoint.
 */
async function syncFormDataOnly(datapoint) {
  try {
    if (datapoint.uuid) {
      return { id: datapoint.uuid };
    } else {
      const formDataObject = datapoint.formData;
      const bodyFormData = new FormData();

      bodyFormData.append("data", JSON.stringify(formDataObject));
      bodyFormData.append("dataset", datapoint.datasetID);
      bodyFormData.append("draft_id", datapoint.draft_id);
      bodyFormData.append("latitude", datapoint.latitude);
      bodyFormData.append("longitude", datapoint.longitude);
      bodyFormData.append("timestamp", datapoint.timestamp);
      bodyFormData.append("submit_time", datapoint.submit_time);

      return apiCallToSaveDatapoint(datapoint.datasetID, bodyFormData);
    }
  } catch (error) {
    throw error;
  }
}

/**
 * Syncs form data along with files for a given datapoint.
 *
 * @param {number} id - ID of the datapoint to sync.
 * @returns {Object} - Object with status information.
 * @throws {Error} - Throws an error if there is an issue syncing form data with files.
 */
async function syncFormDataWithFiles(id) {
  try {
    let datapoint = await db.datapoints.where("id").equals(id).first();
    const formDataObject = await datapoint.formData;

    const fileInfo = await createFileInfo(id, datapoint.uuid);

    const bodyFormData = new FormData();
    bodyFormData.append("data", JSON.stringify(formDataObject));
    bodyFormData.append("dataset", datapoint.datasetID);
    bodyFormData.append("files", JSON.stringify(fileInfo.files));

    return putDatapoint(datapoint.datasetID, datapoint.uuid, bodyFormData);
  } catch (error) {
    throw error;
  }
}

/**
 * Updates the UUID of a datapoint in the local IndexedDB database.
 *
 * @param {number} id - ID of the datapoint to update (ID is unique in indexedDB).
 * @param {string} uuid - New UUID for the datapoint (UUID is unique in backend DB).
 * @returns {string} - Updated UUID.
 */
async function updateDatapointUUID(id, uuid) {
  try {
    await db.datapoints.where("id").equals(id).modify({ uuid: uuid });
    return uuid;
  } catch (error) {
    throw error;
  }
}

/**
 * Syncs a single datapoint, including form data and associated files, with the server.
 *
 * @param {number} id - ID of the datapoint to sync.
 * @param {function} handleLogout - Function to handle logout on error.
 * @returns {boolean} - Indicates whether the sync was successful.
 */
async function syncOneDatapoint(id, handleLogout,getAllCounts=null) {
  try {
    // There is no data in indexDB, which the user is trying to access:
    // User should be logged in, if indexDB is needed.
    let datapoint = await db.datapoints.where("id").equals(id).first();

    if (datapoint && datapoint?.syncing === true) {
      return false;
    }

    await db.datapoints.where("id").equals(id).modify({ syncing: true });

    const formDataResponse = await syncFormDataOnly(datapoint);
    let uuid;

    if (formDataResponse.id) {
      uuid = formDataResponse.id;
    } else {
      uuid = await updateDatapointUUID(datapoint.id, formDataResponse.data.id);
    }

    const uploadFilesResponse = await uploadFilesToS3(id, uuid);

    if (uploadFilesResponse === true) {
      try {
        const syncFilesResponse = await syncFormDataWithFiles(id);
        if (syncFilesResponse.status === 200) {
          const deletion_response = deleteDatapointAndFiles(datapoint.id);
          if(deletion_response === false){
            throw new Error("Deletion failed");
          }
          if(getAllCounts){
            getAllCounts()
          }
          return deletion_response
        } else {
          console.error("API call did not return a 200 status code");
          throw new Error("formdataWithFilesFailed");
        }
      } catch (error) {
        throw error;
      }
    }
    await db.datapoints.where("id").equals(id).modify({ syncing: false });
      
  } catch (error) {
    console.error("An error occurred:", error);
    await db.datapoints.where("id").equals(id).modify({ syncing: false });
    return handleApiError(error);
  }
}

/**
 * Deletes a datapoint and its associated file data from the local IndexedDB database.
 *
 * @param {number} datapointID - ID of the datapoint to delete.
 * @returns {boolean} - Indicates whether the deletion was successful.
 */
async function deleteDatapointAndFiles(datapointID) {
  try {
    // Delete datapoint
    const deleteDatapoint = await db.datapoints
      .where("id")
      .equals(datapointID)
      .delete();

    // Delete associated fileData
    const deleteFileData = await db.fileData
      .where("datapointID")
      .equals(datapointID)
      .delete();

    // Check if both operations were successful
    if (deleteDatapoint) {
      return true;
    } else {
      console.error("Error deleting datapoint or fileData.");
      return false;
    }
  } catch (error) {
    console.error("An error occurred during deletion:", error);
    return false;
  }
}

async function handleSyncAll(handleLogout){
  try {
    const datapoints = await db.datapoints.toArray();
    for (const datapoint of datapoints) {
      await handleSyncOne(datapoint.id,handleLogout);
    }
  } catch (error) {
  console.log("Error syncing formdata/datapoints to online:", error);
  }
};

async function handleSyncOne(id, handleLogout){
  try {
    const response = await syncOneDatapoint(id, handleLogout);

    if (response === true) {
      console.log("passed sync for", id)
    } 
    // else {
    //   throw new Error("Deletion failed");
    // }
  } catch (error) {
    console.log("Error syncing datapoint", id);
  }
};


export {
  addDatapoint,
  getAllDataPoints,
  syncOneDatapoint,
  updateS3FileUrl,
  updateS3UrlFormdata,
  handleSyncAll,
  handleSyncOne,
  deleteDatapointAndFiles
};
